import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, ArrowLeft, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { format, addDays, isBefore, startOfDay } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookAppointment = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  const [formData, setFormData] = useState({
    concern: '',
    notes: ''
  });

  const fetchAvailableSlots = useCallback(async (date) => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/appointments/available-slots?date=${date}`);
      setAvailableSlots(response.data.available_slots);
      setSelectedSlot(null);
    } catch (error) {
      toast.error('Failed to fetch available slots');
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate, fetchAvailableSlots]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to book an appointment');
      navigate('/login');
      return;
    }

    if (!selectedDate || !selectedSlot) {
      toast.error('Please select a date and time slot');
      return;
    }

    if (!formData.concern.trim()) {
      toast.error('Please describe your concern');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/appointments`,
        {
          date: format(selectedDate, 'yyyy-MM-dd'),
          time_slot: selectedSlot,
          concern: formData.concern,
          notes: formData.notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setBookingComplete(true);
      toast.success('Appointment booked successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    const day = date.getDay();
    // Disable past dates and Sundays (0)
    return isBefore(date, today) || day === 0;
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-4" data-testid="booking-complete">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-[#4A6FA5]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-[#4A6FA5]" />
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#1E2A4A] mb-4">
            Appointment Confirmed!
          </h2>
          <div className="bg-[#F5F7FA] rounded-xl p-4 mb-6">
            <p className="text-sm text-[#7A8FA0] mb-2">Your appointment is scheduled for:</p>
            <p className="text-lg font-semibold text-[#1E2A4A]">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </p>
            <p className="text-[#4A6FA5] font-medium">{selectedSlot}</p>
          </div>
          <p className="text-sm text-[#7A8FA0] mb-6">
            Dr. Majmundar's team will send you a confirmation message on WhatsApp.
            Please arrive 10 minutes before your scheduled time.
          </p>
          <div className="flex gap-3">
            <Link to="/portal" className="flex-1">
              <Button variant="outline" className="w-full">
                View in Portal
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button className="w-full bg-[#1E2A4A] hover:bg-[#4A6FA5] text-white">
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="book-appointment-page">
      {/* Header */}
      <div className="bg-[#1E2A4A] py-12">
        <div className="container-custom">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-white">
            Book an Appointment
          </h1>
          <p className="text-[#E8EEF5]/60 mt-2">
            Schedule your consultation with Dr. Drumil D Majmundar
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="container-custom py-8">
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { num: 1, label: 'Select Date & Time' },
            { num: 2, label: 'Your Details' },
            { num: 3, label: 'Confirm' }
          ].map((s, index) => (
            <div key={s.num} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s.num 
                  ? 'bg-[#4A6FA5] text-white' 
                  : 'bg-white text-[#7A8FA0] border border-[#E8EEF5]'
              }`}>
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span className={`ml-2 text-sm hidden sm:inline ${
                step >= s.num ? 'text-[#1E2A4A]' : 'text-[#7A8FA0]'
              }`}>
                {s.label}
              </span>
              {index < 2 && (
                <div className={`w-12 h-px mx-4 ${
                  step > s.num ? 'bg-[#4A6FA5]' : 'bg-[#E8EEF5]'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Auth Warning */}
        {!isAuthenticated && (
          <div className="bg-[#4A6FA5]/10 border border-[#4A6FA5]/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#4A6FA5] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-[#1E2A4A] font-medium">Login required</p>
              <p className="text-sm text-[#7A8FA0]">
                Please <Link to="/login" className="text-[#4A6FA5] underline">login or register</Link> to book an appointment.
              </p>
            </div>
          </div>
        )}

        {/* Step 1: Date & Time Selection */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Calendar */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E2A4A] mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[#4A6FA5]" />
                Select Date
              </h3>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={isDateDisabled}
                className="rounded-xl border-0"
                data-testid="appointment-calendar"
              />
              <p className="text-xs text-[#7A8FA0] mt-4">
                Clinic hours: Monday - Saturday, 10:00 AM - 7:00 PM
              </p>
            </div>

            {/* Time Slots */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E2A4A] mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#4A6FA5]" />
                Select Time Slot
              </h3>
              
              {!selectedDate ? (
                <div className="text-center py-12 text-[#7A8FA0]">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Please select a date first</p>
                </div>
              ) : slotsLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-[#4A6FA5] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#7A8FA0]">Loading available slots...</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-12 text-[#7A8FA0]">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No slots available for this date</p>
                  <p className="text-sm mt-2">Please select another date</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => handleSlotSelect(slot)}
                      className={`p-3 rounded-xl text-sm font-medium transition-all ${
                        selectedSlot === slot
                          ? 'bg-[#4A6FA5] text-white'
                          : 'bg-[#F5F7FA] text-[#1E2A4A] hover:bg-[#E8EEF5]'
                      }`}
                      data-testid={`time-slot-${slot.replace(/\s/g, '-')}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              )}

              {selectedDate && selectedSlot && (
                <div className="mt-6 p-4 bg-[#4A6FA5]/10 rounded-xl">
                  <p className="text-sm text-[#1E2A4A]">
                    <strong>Selected:</strong> {format(selectedDate, 'EEEE, MMMM d')} at {selectedSlot}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-xl mx-auto bg-white rounded-2xl p-8 shadow-sm"
          >
            <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E2A4A] mb-6">
              Tell us about your concern
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                  Primary Concern *
                </Label>
                <Input
                  value={formData.concern}
                  onChange={(e) => setFormData({ ...formData, concern: e.target.value })}
                  placeholder="e.g. Knee pain, ACL injury, Joint replacement consultation..."
                  className="mt-1"
                  data-testid="concern-input"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-wide uppercase text-[#7A8FA0]">
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional information about your symptoms, previous treatments, or questions..."
                  rows={4}
                  className="mt-1"
                  data-testid="notes-input"
                />
              </div>
            </div>

            {/* Appointment Summary */}
            <div className="mt-6 p-4 bg-[#F5F7FA] rounded-xl">
              <p className="text-xs text-[#7A8FA0] uppercase tracking-wide mb-2">Appointment Details</p>
              <p className="text-[#1E2A4A] font-medium">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-[#4A6FA5]">{selectedSlot}</p>
              {user && (
                <p className="text-sm text-[#7A8FA0] mt-2">
                  Booking as: {user.name} ({user.email})
                </p>
              )}
            </div>
          </motion.div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-xl mx-auto bg-white rounded-2xl p-8 shadow-sm"
          >
            <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E2A4A] mb-6">
              Confirm Your Appointment
            </h3>

            <div className="space-y-4">
              <div className="p-4 bg-[#F5F7FA] rounded-xl">
                <p className="text-xs text-[#7A8FA0] uppercase tracking-wide mb-2">Date & Time</p>
                <p className="text-lg font-semibold text-[#1E2A4A]">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </p>
                <p className="text-[#4A6FA5] font-medium">{selectedSlot}</p>
              </div>

              <div className="p-4 bg-[#F5F7FA] rounded-xl">
                <p className="text-xs text-[#7A8FA0] uppercase tracking-wide mb-2">Your Concern</p>
                <p className="text-[#1E2A4A]">{formData.concern}</p>
                {formData.notes && (
                  <p className="text-sm text-[#7A8FA0] mt-2">{formData.notes}</p>
                )}
              </div>

              {user && (
                <div className="p-4 bg-[#F5F7FA] rounded-xl">
                  <p className="text-xs text-[#7A8FA0] uppercase tracking-wide mb-2">Patient Details</p>
                  <p className="text-[#1E2A4A] font-medium">{user.name}</p>
                  <p className="text-sm text-[#7A8FA0]">{user.email}</p>
                  <p className="text-sm text-[#7A8FA0]">{user.phone}</p>
                </div>
              )}

              <div className="p-4 bg-[#1E2A4A]/5 rounded-xl border border-[#1E2A4A]/10">
                <p className="text-xs text-[#1E2A4A]">
                  📍 <strong>Location:</strong> 3rd Floor, 317 Maple Trade Centre, Memnagar, Ahmedabad
                </p>
                <p className="text-xs text-[#7A8FA0] mt-2">
                  Please arrive 10 minutes before your appointment time with any relevant medical records.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 max-w-xl mx-auto">
          {step > 1 ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          ) : (
            <div />
          )}

          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && (!selectedDate || !selectedSlot)}
              className="bg-[#1E2A4A] hover:bg-[#4A6FA5] text-white flex items-center gap-2"
              data-testid="next-step-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !isAuthenticated}
              className="bg-[#4A6FA5] hover:bg-[#1E2A4A] text-white flex items-center gap-2"
              data-testid="confirm-booking-btn"
            >
              {loading ? 'Booking...' : 'Confirm Appointment'}
              <Check className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookAppointment;
